import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { useAuthStore } from '@/store/authStore';
import { initializeAuth } from '@/store/authStore';

// Pages
import LoginPage from '@/pages/auth/LoginPage';
import DashboardPage from '@/pages/dashboard/DashboardPage';
import ClientsPage from '@/pages/clients/ClientsPage';
import ClientDetailPage from '@/pages/clients/ClientDetailPage';
import LoadingPage from '@/pages/LoadingPage';
import UsersPage from '@/pages/users/UsersPage';
import SettingsPage from '@/pages/settings/SettingsPage';
import WhatsAppPage from '@/pages/whatsapp/WhatsAppPage';
import EmailPage from '@/pages/email/EmailPage';
import ContactsPage from '@/pages/contacts/ContactsPage';
import BackupPage from '@/pages/backup/BackupPage';
import AIAssistantProPage from '@/pages/ai-assistant-pro/AIAssistantProPage';
import ProfilePage from '@/pages/profile/ProfilePage';
import PracticesPage from '@/pages/practices/PracticesPage';
import PracticeDetailPage from '@/pages/practices/PracticeDetailPage';
import PracticeFormPage from '@/pages/practices/PracticeFormPage';
import PracticeEditPage from '@/pages/practices/PracticeEditPage';
import PracticeTypesPage from '@/pages/practices/PracticeTypesPage';
import HistoricalPracticeFormPage from '@/pages/practices/HistoricalPracticeFormPage';
import ActivityTypesPage from '@/pages/activities/ActivityTypesPage';
import ModulesPage from '@/pages/modules/ModulesPage';
import ArchivedPracticesPage from '@/pages/practices/ArchivedPracticesPage';
import CalendarPage from '@/pages/calendar/CalendarPage';
import AgendaPage from '@/pages/agenda/AgendaPage';
import ActivitiesPage from '@/pages/activities/ActivitiesPage';
import ActivityDetailPage from '@/pages/activities/ActivityDetailPage';
import ActivityEditPage from '@/pages/activities/ActivityEditPage';
import DocumentsPage from '@/pages/documents/DocumentsPage';
import ScannerPage from '@/pages/scanner/ScannerPage';
import OperatorStatsPage from '@/pages/admin/OperatorStatsPage';
import StaffMessagingPage from '@/pages/staff-messaging/StaffMessagingPage';
import StaffMessagingAdminPage from '@/pages/admin/StaffMessagingAdminPage';
import BillingListPage from '@/pages/billing/BillingListPage';
import BillingDocumentPage from '@/pages/billing/BillingDocumentPage';
import BillingPriceListPage from '@/pages/billing/BillingPriceListPage';
import BillingSettingsPage from '@/pages/billing/BillingSettingsPage';
import BillingAgingPage from '@/pages/billing/BillingAgingPage';

const TimbraPage = lazy(() => import('@/pages/attendance/TimbraPage'));
const TimbraCertificatoPage = lazy(() => import('@/pages/attendance/TimbraCertificatoPage'));
const TimbraturaMePage = lazy(() => import('@/pages/attendance/TimbraturaMePage'));
const AdminTimbraturePage = lazy(() => import('@/pages/admin/AdminTimbraturePage'));
const AdminTimbratureSettingsPage = lazy(() => import('@/pages/admin/AdminTimbratureSettingsPage'));

// Layout
import AppLayout from '@/components/layout/AppLayout';
import AuthLayout from '@/components/layout/AuthLayout';
import { isOperatorMobileAllowedPath, isOperatorMobileLock } from '@/utils/isProbablyMobile';

// Styles
import '@/styles/globals.css';

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected Route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Admin-only Route component
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Solo gli amministratori possono accedere
  if (user?.role !== 'ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function BillingRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.canAccessQuotesAndAccounts) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function BillingDocumentGate() {
  const { id } = useParams();
  const location = useLocation();
  return (
    <BillingRoute>
      <BillingDocumentPage key={`${id || 'nuovo'}${location.search}`} />
    </BillingRoute>
  );
}

function WhatsappManagerRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'ADMIN' && user?.role !== 'WHATSAPP_MANAGER') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Route guard: blocca accesso alle pagine di timbratura personale per utenti senza flag
function AttendanceRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.requiresAttendance === false) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Public Route component (redirect to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function OperatorMobileRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  const location = useLocation();

  if (isOperatorMobileLock(user) && !isOperatorMobileAllowedPath(location.pathname)) {
    return <Navigate to="/timbra" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { isLoading } = useAuthStore();

  useEffect(() => {
    // Initialize authentication on app start
    initializeAuth();
  }, []);

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="min-h-screen bg-background">
          <Routes>
            <Route
              path="/timbra"
              element={
                <Suspense fallback={<LoadingPage />}>
                  <TimbraPage />
                </Suspense>
              }
            />
            <Route
              path="/timbra/certificato"
              element={
                <Suspense fallback={<LoadingPage />}>
                  <TimbraCertificatoPage />
                </Suspense>
              }
            />

            {/* Public routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <AuthLayout>
                    <LoginPage />
                  </AuthLayout>
                </PublicRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <OperatorMobileRoute>
                    <AppLayout />
                  </OperatorMobileRoute>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="clients/:id" element={<ClientDetailPage />} />
              <Route path="practices" element={<PracticesPage />} />
              <Route path="practices/archived" element={<ArchivedPracticesPage />} />
              <Route path="practices/new" element={<PracticeFormPage />} />
              <Route path="practices/historical/new" element={<HistoricalPracticeFormPage />} />
              <Route path="practices/:id" element={<PracticeDetailPage />} />
              <Route path="practices/:id/edit" element={<PracticeEditPage />} />
              <Route
                path="preventivi-e-conti"
                element={
                  <BillingRoute>
                    <BillingListPage />
                  </BillingRoute>
                }
              />
              <Route
                path="preventivi-e-conti/nuovo"
                element={<BillingDocumentGate />}
              />
              <Route
                path="preventivi-e-conti/listino"
                element={
                  <BillingRoute>
                    <BillingPriceListPage />
                  </BillingRoute>
                }
              />
              <Route
                path="preventivi-e-conti/impostazioni"
                element={
                  <BillingRoute>
                    <BillingSettingsPage />
                  </BillingRoute>
                }
              />
              <Route
                path="preventivi-e-conti/scadenziario"
                element={
                  <BillingRoute>
                    <BillingAgingPage />
                  </BillingRoute>
                }
              />
              <Route
                path="preventivi-e-conti/:id"
                element={<BillingDocumentGate />}
              />
              <Route path="practice-types" element={<PracticeTypesPage />} />
              <Route path="activities" element={<ActivitiesPage />} />
              <Route path="activities/:id" element={<ActivityDetailPage />} />
              <Route path="activities/:id/edit" element={<ActivityEditPage />} />
              <Route path="activity-types" element={<ActivityTypesPage />} />
              <Route path="modules" element={<ModulesPage />} />
              <Route path="whatsapp" element={<WhatsAppPage />} />
              <Route path="email" element={<EmailPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="messaggi" element={<StaffMessagingPage />} />
              <Route path="agenda" element={<AgendaPage />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="scanner" element={<ScannerPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="backup" element={<BackupPage />} />
              <Route path="ai-assistant-pro" element={<AIAssistantProPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route
                path="timbratura/me"
                element={
                  <AttendanceRoute>
                    <Suspense fallback={<LoadingPage />}>
                      <TimbraturaMePage />
                    </Suspense>
                  </AttendanceRoute>
                }
              />
              <Route 
                path="users" 
                element={
                  <AdminRoute>
                    <UsersPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="settings" 
                element={
                  <WhatsappManagerRoute>
                    <SettingsPage />
                  </WhatsappManagerRoute>
                } 
              />
              <Route
                path="admin/operator-stats"
                element={
                  <AdminRoute>
                    <OperatorStatsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/staff-messaging"
                element={
                  <AdminRoute>
                    <StaffMessagingAdminPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/timbrature"
                element={
                  <AdminRoute>
                    <Suspense fallback={<LoadingPage />}>
                      <AdminTimbraturePage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/timbrature/settings"
                element={
                  <AdminRoute>
                    <Suspense fallback={<LoadingPage />}>
                      <AdminTimbratureSettingsPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
            </Route>

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
        <Toaster />
      </Router>
    </QueryClientProvider>
  );
}

export default App;
